import {
  AddCircleOutlined,
  DragIndicatorOutlined,
  RemoveCircleOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
  getBranchPricingSettings,
  getBusinessPricing,
  patchBranchPricingSettings,
  patchBusinessPricing,
} from '../../api/branchesAPI';
import { getCurrentUser } from '../../api/usersAPI';
import NavBar from '../layout/NavBar';

function createPricingRow() {
  return {
    name: '',
    quantity: '',
    part_price: '',
    labor_price: '',
  };
}

function normalizePricing(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((item) => ({
    name: item?.name ?? item?.description ?? '',
    quantity: item?.quantity ?? '',
    part_price: item?.part_price ?? '',
    labor_price: item?.labor_price ?? '',
  }));
}

function resolveBranchLabel(branch) {
  return branch.name ? `${branch.name} - ${branch.address}` : branch.address;
}

function PricingEditor({
  title,
  helperText,
  rows,
  onRowChange,
  onMoveRow,
  onAddRow,
  onRemoveRow,
  onSave,
  saveLabel,
  error,
  success,
  emptyMessage,
}) {
  return (
    <>
      <Typography variant='h5' sx={{ mb: 2 }}>
        {title}
      </Typography>
      {helperText && (
        <Typography sx={{ mb: 2, color: '#4b5563' }}>
          {helperText}
        </Typography>
      )}
      {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity='success' sx={{ mb: 2 }}>{success}</Alert>}

      <Stack spacing={1.5}>
        {rows.map((row, index) => (
          <Box
            key={`${title}-${index}`}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto minmax(220px, 1.8fr) 90px 120px 120px auto',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Stack spacing={0.5} sx={{ alignItems: 'center' }}>
              <Button
                size='small'
                onClick={() => onMoveRow(index, index - 1)}
                disabled={index === 0}
                sx={{ minWidth: 0, p: 0.5 }}
                aria-label={`Move ${row.name || `row ${index + 1}`} up`}
              >
                <DragIndicatorOutlined fontSize='small' sx={{ transform: 'rotate(-90deg)' }} />
              </Button>
              <Button
                size='small'
                onClick={() => onMoveRow(index, index + 1)}
                disabled={index === rows.length - 1}
                sx={{ minWidth: 0, p: 0.5 }}
                aria-label={`Move ${row.name || `row ${index + 1}`} down`}
              >
                <DragIndicatorOutlined fontSize='small' sx={{ transform: 'rotate(90deg)' }} />
              </Button>
            </Stack>
            <TextField
              label='Name'
              variant='outlined'
              size='small'
              value={row.name}
              onChange={(event) => onRowChange(index, 'name', event.target.value)}
            />
            <TextField
              label='Qty'
              variant='outlined'
              size='small'
              value={row.quantity}
              onChange={(event) => onRowChange(index, 'quantity', event.target.value)}
            />
            <TextField
              label='Parts'
              variant='outlined'
              size='small'
              value={row.part_price}
              onChange={(event) => onRowChange(index, 'part_price', event.target.value)}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                },
              }}
            />
            <TextField
              label='Labor'
              variant='outlined'
              size='small'
              value={row.labor_price}
              onChange={(event) => onRowChange(index, 'labor_price', event.target.value)}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                },
              }}
            />
            <Button
              color='error'
              onClick={() => onRemoveRow(index)}
              disabled={rows.length === 1}
              startIcon={<RemoveCircleOutlined />}
            >
              Remove
            </Button>
          </Box>
        ))}
      </Stack>

      {rows.length === 0 && (
        <Typography sx={{ color: '#6b7280', mb: 2 }}>
          {emptyMessage}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button onClick={onAddRow} startIcon={<AddCircleOutlined />}>
          Add Item
        </Button>
        <Button variant='contained' onClick={onSave}>
          {saveLabel}
        </Button>
      </Box>
    </>
  );
}

export default function Settings() {
  const [user, setUser] = useState(null);
  const [branchOptions, setBranchOptions] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [businessPricingRows, setBusinessPricingRows] = useState([createPricingRow()]);
  const [branchPricingRows, setBranchPricingRows] = useState([createPricingRow()]);
  const [effectiveBranchPricingRows, setEffectiveBranchPricingRows] = useState([]);
  const [branchName, setBranchName] = useState('');
  const [businessError, setBusinessError] = useState('');
  const [branchError, setBranchError] = useState('');
  const [pageError, setPageError] = useState('');
  const [businessSuccess, setBusinessSuccess] = useState('');
  const [branchSuccess, setBranchSuccess] = useState('');

  const availableBranches = useMemo(() => branchOptions, [branchOptions]);
  const availableBranchIds = useMemo(
    () => availableBranches.map((branch) => branch.id),
    [availableBranches]
  );

  useEffect(() => {
    let isMounted = true;

    async function initializeSettings() {
      try {
        const [userResponse, businessResponse] = await Promise.all([
          getCurrentUser(),
          getBusinessPricing(),
        ]);

        if (!isMounted) {
          return;
        }

        const userData = userResponse.data;
        const userBranches = (userData.all_branches ?? []).map((branch) => ({
          ...branch,
          id: String(branch.id),
        }));
        const initialBranchId = userData.current_branch
          ? String(userData.current_branch)
          : (userBranches[0]?.id ?? '');

        setUser(userData);
        setBranchOptions(userBranches);
        setSelectedBranchId(initialBranchId);

        const normalizedBusinessPricing = normalizePricing(businessResponse.data.default_pricing);
        setBusinessPricingRows(
          normalizedBusinessPricing.length > 0
            ? normalizedBusinessPricing
            : [createPricingRow()]
        );

        if (initialBranchId) {
          const branchResponse = await getBranchPricingSettings(initialBranchId);

          if (!isMounted) {
            return;
          }

          setBranchName(branchResponse.data.name || branchResponse.data.address || '');

          const normalizedBranchPricing = normalizePricing(branchResponse.data.default_pricing);
          setBranchPricingRows(
            normalizedBranchPricing.length > 0
              ? normalizedBranchPricing
              : [createPricingRow()]
          );
          setEffectiveBranchPricingRows(
            normalizePricing(branchResponse.data.effective_default_pricing)
          );
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        if (isMounted) {
          setPageError('Unable to load settings right now.');
        }
      }
    }

    initializeSettings();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (
      selectedBranchId &&
      availableBranchIds.length > 0 &&
      !availableBranchIds.includes(selectedBranchId)
    ) {
      setSelectedBranchId(availableBranchIds[0]);
      return;
    }

    if (!selectedBranchId) {
      if (availableBranchIds.length > 0) {
        setSelectedBranchId(availableBranchIds[0]);
        return;
      }

      setBranchName('');
      setBranchPricingRows([createPricingRow()]);
      setEffectiveBranchPricingRows([]);
      return;
    }

    let isMounted = true;

    async function loadBranchPricing() {
      try {
        const branchResponse = await getBranchPricingSettings(selectedBranchId);

        if (!isMounted) {
          return;
        }

        setBranchName(branchResponse.data.name || branchResponse.data.address || '');

        const normalizedBranchPricing = normalizePricing(branchResponse.data.default_pricing);
        setBranchPricingRows(
          normalizedBranchPricing.length > 0
            ? normalizedBranchPricing
            : [createPricingRow()]
        );
        setEffectiveBranchPricingRows(
          normalizePricing(branchResponse.data.effective_default_pricing)
        );
        setBranchError('');
      } catch (error) {
        console.error('Error loading branch pricing:', error);
        if (isMounted) {
          setBranchError('Unable to load branch pricing.');
        }
      }
    }

    loadBranchPricing();

    return () => {
      isMounted = false;
    };
  }, [availableBranchIds, selectedBranchId]);

  const updateBusinessRow = (index, field, value) => {
    setBusinessPricingRows((currentRows) =>
      currentRows.map((row, rowIndex) => (
        rowIndex === index ? { ...row, [field]: value } : row
      ))
    );
    setBusinessSuccess('');
  };

  const updateBranchRow = (index, field, value) => {
    setBranchPricingRows((currentRows) =>
      currentRows.map((row, rowIndex) => (
        rowIndex === index ? { ...row, [field]: value } : row
      ))
    );
    setBranchSuccess('');
  };

  const addBusinessRow = () => {
    setBusinessPricingRows((currentRows) => [...currentRows, createPricingRow()]);
    setBusinessSuccess('');
  };

  const moveBusinessRow = (fromIndex, toIndex) => {
    setBusinessPricingRows((currentRows) => {
      if (toIndex < 0 || toIndex >= currentRows.length) {
        return currentRows;
      }

      const nextRows = [...currentRows];
      const [movedRow] = nextRows.splice(fromIndex, 1);
      nextRows.splice(toIndex, 0, movedRow);
      return nextRows;
    });
    setBusinessSuccess('');
  };

  const addBranchRow = () => {
    setBranchPricingRows((currentRows) => [...currentRows, createPricingRow()]);
    setBranchSuccess('');
  };

  const moveBranchRow = (fromIndex, toIndex) => {
    setBranchPricingRows((currentRows) => {
      if (toIndex < 0 || toIndex >= currentRows.length) {
        return currentRows;
      }

      const nextRows = [...currentRows];
      const [movedRow] = nextRows.splice(fromIndex, 1);
      nextRows.splice(toIndex, 0, movedRow);
      return nextRows;
    });
    setBranchSuccess('');
  };

  const removeBusinessRow = (index) => {
    if (businessPricingRows.length === 1) {
      return;
    }

    setBusinessPricingRows((currentRows) => currentRows.filter((_, rowIndex) => rowIndex !== index));
    setBusinessSuccess('');
  };

  const removeBranchRow = (index) => {
    if (branchPricingRows.length === 1) {
      return;
    }

    setBranchPricingRows((currentRows) => currentRows.filter((_, rowIndex) => rowIndex !== index));
    setBranchSuccess('');
  };

  const sanitizeRows = (rows) => rows
    .filter((row) => row.name || row.quantity || row.part_price || row.labor_price)
    .map((row) => ({
      name: row.name,
      quantity: row.quantity,
      part_price: row.part_price,
      labor_price: row.labor_price,
    }));

  const handleBusinessSave = async () => {
    try {
      const response = await patchBusinessPricing({
        default_pricing: sanitizeRows(businessPricingRows),
      });

      const normalizedBusinessPricing = normalizePricing(response.data.default_pricing);
      setBusinessPricingRows(
        normalizedBusinessPricing.length > 0
          ? normalizedBusinessPricing
          : [createPricingRow()]
      );
      setBusinessError('');
      setBusinessSuccess('Business default pricing saved.');
    } catch (error) {
      console.error('Error saving business pricing:', error);
      setBusinessError('Unable to save business default pricing.');
    }
  };

  const handleBranchSave = async () => {
    try {
      const response = await patchBranchPricingSettings(selectedBranchId, {
        default_pricing: sanitizeRows(branchPricingRows),
      });

      const normalizedBranchPricing = normalizePricing(response.data.default_pricing);
      setBranchPricingRows(
        normalizedBranchPricing.length > 0
          ? normalizedBranchPricing
          : [createPricingRow()]
      );
      setEffectiveBranchPricingRows(
        normalizePricing(response.data.effective_default_pricing)
      );
      setBranchError('');
      setBranchSuccess('Branch pricing saved.');
    } catch (error) {
      console.error('Error saving branch pricing:', error);
      setBranchError('Unable to save branch pricing.');
    }
  };

  return (
    <Box
      sx={{
        textAlign: 'center',
        minWidth: '519px',
        maxWidth: '75%',
        margin: 'auto',
        pb: 4,
      }}
    >
      <Typography variant='h2' sx={{ mb: 2 }}>Settings</Typography>
      <NavBar />

      {pageError && <Alert severity='error' sx={{ mt: 3 }}>{pageError}</Alert>}

      <Stack spacing={3} sx={{ mt: 4, maxWidth: 1100, mx: 'auto', textAlign: 'left' }}>
        <Card>
          <CardContent>
            <Typography variant='h5' sx={{ mb: 1 }}>
              Profile
            </Typography>
            <Typography><strong>Name:</strong> {user?.full_name || 'Not provided'}</Typography>
            <Typography><strong>Email:</strong> {user?.email || '-'}</Typography>
            <Typography><strong>Business:</strong> {user?.business_name || '-'}</Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <PricingEditor
              title='Business Default Pricing'
              rows={businessPricingRows}
              onRowChange={updateBusinessRow}
              onMoveRow={moveBusinessRow}
              onAddRow={addBusinessRow}
              onRemoveRow={removeBusinessRow}
              onSave={handleBusinessSave}
              saveLabel='Save Business Pricing'
              error={businessError}
              success={businessSuccess}
              emptyMessage='No default business pricing configured.'
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant='h5' sx={{ mb: 2 }}>
              Branch Default Pricing
            </Typography>
            <Typography sx={{ mb: 2, color: '#4b5563' }}>
              Business pricing is the fallback. A branch value replaces it for that branch only.
            </Typography>
            <FormControl fullWidth size='small' sx={{ mb: 2 }}>
              <InputLabel id='settings-branch-select-label'>Branch</InputLabel>
              <Select
                labelId='settings-branch-select-label'
                label='Branch'
                variant='outlined'
                value={selectedBranchId}
                onChange={(event) => {
                  setSelectedBranchId(String(event.target.value));
                  setBranchSuccess('');
                }}
              >
                {availableBranches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {resolveBranchLabel(branch)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography sx={{ mb: 2, color: '#4b5563' }}>
              Editing pricing for: <strong>{branchName || 'No branch selected'}</strong>
            </Typography>

            <PricingEditor
              title='Branch Pricing'
              helperText='These rows override the business default for the selected branch.'
              rows={branchPricingRows}
              onRowChange={updateBranchRow}
              onMoveRow={moveBranchRow}
              onAddRow={addBranchRow}
              onRemoveRow={removeBranchRow}
              onSave={handleBranchSave}
              saveLabel='Save Branch Pricing'
              error={branchError}
              success={branchSuccess}
              emptyMessage='No branch pricing configured.'
            />

            <Box sx={{ mt: 4 }}>
              <Typography variant='h6' sx={{ mb: 2 }}>
                Resolved Pricing Used for This Branch
              </Typography>
              {effectiveBranchPricingRows.length === 0 ? (
                <Typography sx={{ color: '#6b7280' }}>
                  No pricing is currently configured for this branch or its business.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {effectiveBranchPricingRows.map((row, index) => (
                    <Box
                      key={`effective-${index}`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(220px, 1.8fr) 90px 120px 120px',
                        gap: 1,
                        alignItems: 'center',
                      }}
                    >
                      <TextField label='Name' variant='outlined' size='small' value={row.name} InputProps={{ readOnly: true }} />
                      <TextField label='Qty' variant='outlined' size='small' value={row.quantity} InputProps={{ readOnly: true }} />
                      <TextField label='Parts' variant='outlined' size='small' value={row.part_price} InputProps={{ readOnly: true, startAdornment: <InputAdornment position='start'>$</InputAdornment> }} />
                      <TextField label='Labor' variant='outlined' size='small' value={row.labor_price} InputProps={{ readOnly: true, startAdornment: <InputAdornment position='start'>$</InputAdornment> }} />
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
